import React from 'react'
import { Head, Link } from '@inertiajs/react'

const A4_STYLES = {
  width: '210mm',
  height: '297mm',
}

const A5_STYLES = {
  width: '148mm',
  height: '210mm',
}

export default function Print({ account, position, size }) {
  const isA5 = size === 'a5'
  const pageStyles = isA5 ? A5_STYLES : A4_STYLES
  const otherSize = isA5 ? 'a4' : 'a5'

  const handlePrint = () => {
    window.print()
  }

  return (
    <>
      <Head title={`Print Poster - ${position.name}`} />

      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <Link
          href={`/manage/positions/${position.id}/print?size=${otherSize}`}
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Switch to {otherSize.toUpperCase()}
        </Link>
        <button
          onClick={handlePrint}
          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print / Save as PDF
        </button>
        <Link
          href={`/manage/positions/${position.id}/edit`}
          className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Back to Edit
        </Link>
      </div>

      {/* Page Size Indicator - Hidden when printing */}
      <div className="print:hidden fixed top-4 left-4 z-50 bg-black/75 text-white px-3 py-1 rounded-full text-sm font-medium">
        {size.toUpperCase()} Poster
      </div>

      {/* Background for screen view */}
      <div className="min-h-screen bg-gray-300 print:bg-white flex items-center justify-center py-8 print:py-0">
        {/* Poster Container */}
        <div
          style={pageStyles}
          className="shadow-2xl print:shadow-none flex flex-col overflow-hidden"
        >
          {/* Top Half - Blue with white text */}
          <div
            className="flex-1 flex flex-col justify-center items-center text-white"
            style={{ backgroundColor: '#3B82F6' }}
          >
            {/* Account Name */}
            <h1 className={`font-bold text-white/90 mb-4 ${isA5 ? 'text-xl' : 'text-2xl'}`}>
              {account.name}
            </h1>

            {/* Location Name */}
            <h2 className={`font-bold text-center px-8 ${isA5 ? 'text-3xl' : 'text-5xl'}`}>
              {position.name}
            </h2>

            {position.position_type_label && (
              <p className={`text-white/80 mt-3 ${isA5 ? 'text-base' : 'text-xl'}`}>
                {position.position_type_label}
              </p>
            )}

            {/* Promo Text */}
            {position.order_promo_text && (
              <p className={`mt-6 text-white/90 text-center px-12 max-w-prose ${isA5 ? 'text-sm' : 'text-lg'}`}>
                {position.order_promo_text}
              </p>
            )}
          </div>

          {/* Bottom Half - White with QR code */}
          <div className="flex-1 bg-white flex flex-col justify-center items-center">
            {/* QR Code */}
            <div className={`${isA5 ? 'w-40 h-40' : 'w-56 h-56'}`}>
              <div
                className="w-full h-full [&>svg]:w-full [&>svg]:h-full"
                dangerouslySetInnerHTML={{ __html: position.qr_code_svg }}
              />
            </div>

            {/* Scan instruction */}
            <p className={`mt-6 font-semibold text-gray-800 ${isA5 ? 'text-xl' : 'text-3xl'}`}>
              Scan to Order
            </p>

            {/* Location Code */}
            <p className={`mt-3 text-gray-400 font-mono ${isA5 ? 'text-xs' : 'text-sm'}`}>
              {position.qr_code}
            </p>
          </div>
        </div>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: ${isA5 ? 'A5' : 'A4'};
            margin: 0;
          }

          body {
            margin: 0;
            padding: 0;
          }

          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </>
  )
}
